import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useExams } from '../../hooks/useExams';
import { Calendar, Clock, Play, Eye, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const StudentExams = () => {
  const navigate = useNavigate();
  const { exams, loading, error, fetchExams } = useExams({ scope: 'student' });

  const columns = [
    { id: 'title', header: 'Exam Title', accessorKey: 'title', sortable: true },
    { id: 'subject', header: 'Subject', accessorKey: 'subjectName', sortable: true },
    { id: 'class', header: 'Class', accessorKey: 'className' },
    { id: 'examDate', header: 'Date', accessorKey: 'examDate', sortable: true },
    { id: 'startTime', header: 'Start Time', accessorKey: 'startTime' },
    { id: 'duration', header: 'Duration', accessorKey: 'duration' },
    { id: 'status', header: 'Status', accessorKey: 'status', cell: ({ row }) => {
      const status = row.original.status;
      const statusColors = {
        scheduled: 'bg-blue-100 text-blue-800',
        active: 'bg-green-100 text-green-800',
        completed: 'bg-purple-100 text-purple-800',
        graded: 'bg-indigo-100 text-indigo-800'
      };
      return (
        <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
          {status}
        </Badge>
      );
    }},
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const exam = row.original;
        const canTake = exam.status === 'ongoing' && new Date() >= new Date(`${exam.examDate}T${exam.startTime}`);
        const canView = exam.status === 'completed' || exam.status === 'graded';
        
        return (
          <div className="flex items-center gap-2">
            {canTake && (
              <Button 
                size="sm" 
                onClick={() => navigate(`/dashboard/exams/take/${exam.id}`)}
                className="flex items-center gap-1"
              >
                <Play className="w-4 h-4" />
                Take Exam
              </Button>
            )}
            {canView && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => navigate(`/dashboard/exams/results/${exam.id}`)}
                className="flex items-center gap-1"
              >
                <Eye className="w-4 h-4" />
                View Result
              </Button>
            )}
            {exam.status === 'scheduled' && (
              <Button 
                size="sm" 
                variant="outline" 
                disabled
                className="flex items-center gap-1"
              >
                <Clock className="w-4 h-4" />
                Not Available
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  const upcomingExams = exams?.filter(exam => 
    exam.status === 'scheduled' || exam.status === 'ongoing'
  ) || [];

  const completedExams = exams?.filter(exam => 
    exam.status === 'completed' || exam.status === 'graded'
  ) || [];

  const getExamStats = () => {
    return {
      total: exams?.length || 0,
      upcoming: upcomingExams.length,
      completed: completedExams.length,
      available: upcomingExams.filter(exam => {
        const now = new Date();
        const examDateTime = new Date(`${exam.examDate}T${exam.startTime}`);
        return exam.status === 'ongoing' && now >= examDateTime;
      }).length
    };
  };

  const stats = getExamStats();

  const getUpcomingExamCountdown = (exam) => {
    const now = new Date();
    const examDateTime = new Date(`${exam.examDate}T${exam.startTime}`);
    const timeDiff = examDateTime - now;
    
    if (timeDiff <= 0) return 'Available now';
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={() => fetchExams()}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">My Exams</h2>
          <p className="text-gray-600">View and take your scheduled exams</p>
        </div>
        <Button variant="outline" onClick={() => fetchExams()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Exams</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Play className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Available Now</p>
                <p className="text-2xl font-semibold">{stats.available}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-semibold">{stats.upcoming}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-semibold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Available Exams Alert */}
      {stats.available > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-800">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">
                You have {stats.available} exam{stats.available !== 1 ? 's' : ''} available to take right now!
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exams Tabs */}
      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upcoming">Upcoming ({stats.upcoming})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          {upcomingExams.length === 0 ? (
            <EmptyState 
              icon={Clock}
              title="No upcoming exams"
              description="You're all set! No exams scheduled right now."
              action
              actionLabel="Refresh"
              onAction={() => fetchExams()}
            />
          ) : (
            <div className="space-y-4">
              {/* Available Exams */}
              {upcomingExams.filter(exam => {
                const now = new Date();
                const examDateTime = new Date(`${exam.examDate}T${exam.startTime}`);
                return exam.status === 'ongoing' && now >= examDateTime;
              }).length > 0 && (
                <Card className="border-green-200">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <Play className="w-5 h-5" />
                      Available Exams
                    </h3>
                    <div className="space-y-2">
                      {upcomingExams.filter(exam => {
                        const now = new Date();
                        const examDateTime = new Date(`${exam.examDate}T${exam.startTime}`);
                        return exam.status === 'ongoing' && now >= examDateTime;
                      }).map(exam => (
                        <div key={exam.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <p className="font-medium">{exam.title}</p>
                            <p className="text-sm text-gray-600">{exam.subjectName} • {exam.className}</p>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => navigate(`/dashboard/exams/take/${exam.id}`)}
                            className="flex items-center gap-1"
                          >
                            <Play className="w-4 h-4" />
                            Take Now
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Scheduled Exams */}
              <Card>
                <CardContent className="p-0">
                  <DataTable 
                    data={upcomingExams} 
                    columns={columns.map(col => {
                      if (col.id === 'examDate') {
                        return {
                          ...col,
                          cell: ({ row }) => {
                            const exam = row.original;
                            const countdown = getUpcomingExamCountdown(exam);
                            return (
                              <div>
                                <div>{exam.examDate}</div>
                                <div className="text-xs text-gray-500">{countdown}</div>
                              </div>
                            );
                          }
                        };
                      }
                      return col;
                    })} 
                    searchable 
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed">
          {completedExams.length === 0 ? (
            <EmptyState 
              icon={CheckCircle}
              title="No completed exams"
              description="Completed exams and results will appear here."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={completedExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentExams;


